import { formatUnits, type Address } from 'viem'

/** Shown wherever a value is not yet known from chain. Never a fabricated 0. */
export const UNKNOWN = '—'

/**
 * Formats a token amount for display.
 *
 * All arithmetic stays in bigint; `formatUnits` does the decimal shift exactly,
 * then we group the integer part. Returns `UNKNOWN` for undefined so a missing
 * read is visibly missing rather than silently rendered as zero.
 */
export function formatToken(
  value: bigint | undefined,
  decimals: number,
  maxFractionDigits = 4,
): string {
  if (value === undefined) return UNKNOWN
  const raw = formatUnits(value, decimals)
  const [whole, fraction = ''] = raw.split('.')
  const grouped = BigInt(whole).toLocaleString('en-US')
  const trimmed = fraction.slice(0, maxFractionDigits).replace(/0+$/, '')
  if (trimmed) return `${grouped}.${trimmed}`
  // A nonzero amount smaller than the display precision must not read as "0",
  // or a shortfall of one wei renders as "Need 0 more".
  if (value !== 0n && whole === '0') return `<0.${'0'.repeat(maxFractionDigits - 1)}1`
  return grouped
}

/** Formats a plain count (supply, forge counts). */
export function formatCount(value: bigint | number | undefined): string {
  if (value === undefined) return UNKNOWN
  return Number(value).toLocaleString('en-US')
}

/** `0x71C7…9E4f` — the exact abbreviation style used in the approved design. */
export function shortenAddress(address: Address | string | undefined): string {
  if (!address) return UNKNOWN
  if (address.length < 12) return address
  return `${address.slice(0, 6)}…${address.slice(-4)}`
}

/** `#101–#104` for a contiguous run, otherwise an explicit list. */
export function formatTokenIds(ids: bigint[]): string {
  if (ids.length === 0) return UNKNOWN
  if (ids.length === 1) return `#${ids[0]}`
  const contiguous = ids.every((id, i) => i === 0 || id === ids[i - 1] + 1n)
  if (contiguous) return `#${ids[0]}–#${ids[ids.length - 1]}`
  return ids.map((id) => `#${id}`).join(', ')
}

/** Turns an unknown thrown value into a single line safe to render. */
export function readableError(error: unknown): string {
  if (!error) return ''
  const anyError = error as { shortMessage?: string; details?: string; message?: string }
  const message = anyError.shortMessage || anyError.details || anyError.message || String(error)
  return message.split('\n')[0].slice(0, 220)
}

/** True when the user rejected the request in their wallet. */
export function isUserRejection(error: unknown): boolean {
  if (!error) return false
  const anyError = error as { name?: string; code?: number; cause?: { code?: number } }
  if (anyError.name === 'UserRejectedRequestError') return true
  if (anyError.code === 4001 || anyError.cause?.code === 4001) return true
  return /user rejected|user denied|request rejected/i.test(readableError(error))
}
